import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Chip,
    Button,
    Skeleton,
    Image,
} from "@heroui/react";
import { LuArrowLeft, LuPencil } from "react-icons/lu";
import { useState } from "react";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
    ImageLightbox,
    NoImageFallback,
} from "@/components/ui";
import { formatCurrency, formatDate, formatThousands } from "@/utils";
import { useUser } from "@/context/UserContext";

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [lightbox, setLightbox] = useState({ isOpen: false, index: 0 });

    const { data, isLoading, error, refetch } = useFetchData(
        `materials/${id}/show`,
    );

    if (error) {
        const is404 = error?.response?.status === 404;
        return (
            <div className="p-6">
                {is404 ? (
                    <EmptyState
                        title="Data Tidak Ditemukan"
                        description="Produk yang Anda cari tidak ditemukan atau sudah dihapus."
                        actionLabel="Kembali ke Daftar"
                        onAction={() => navigate("/products")}
                    />
                ) : (
                    <ErrorState
                        title="Gagal Memuat Detail Produk"
                        message={error?.message}
                        onRetry={refetch}
                    />
                )}
            </div>
        );
    }

    const material = data;
    const prices = material?.prices || [];
    const images = material?.images || [];
    const stockSummary = material?.stock_summary || [];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Detail Produk"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Produk", href: "/products" },
                        isLoading
                            ? { label: "..." }
                            : { label: material?.name },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuArrowLeft />}
                        onPress={() => navigate("/products")}
                    >
                        Kembali
                    </Button>
                    {hasPermission("material.edit") && (
                        <Button
                            as={Link}
                            to={`/products/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {/* Informasi Produk */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Informasi Produk
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <Skeleton className="h-4 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-40 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <InfoItem
                                label="Nama Produk"
                                value={material?.name}
                            />
                            <InfoItem
                                label="Kategori"
                                value={material?.category?.name || "-"}
                            />
                            <InfoItem
                                label="Sub Kategori"
                                value={material?.sub_category?.name || "-"}
                            />
                            <div className="flex flex-col gap-1">
                                <ThemedText
                                    size="xs"
                                    variant="secondary"
                                    className="font-medium"
                                >
                                    Status Keaktifan
                                </ThemedText>
                                <div>
                                    <Chip
                                        color={
                                            material?.active_status
                                                ?.badge_color || "default"
                                        }
                                        size="sm"
                                        variant="flat"
                                        className="font-medium"
                                    >
                                        {material?.active_status?.name ||
                                            "Unknown"}
                                    </Chip>
                                </div>
                            </div>
                            {material?.description && (
                                <div className="sm:col-span-2 lg:col-span-3">
                                    <InfoItem
                                        label="Deskripsi"
                                        value={material.description}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Daftar Harga */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Daftar Harga
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {prices.length} satuan harga
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 w-8 shrink-0 rounded-lg" />
                                    <Skeleton className="h-5 flex-2 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : prices.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Belum ada satuan harga.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Satuan</th>
                                    <th>Harga</th>
                                    <th>Faktor Konversi</th>
                                    <th>Default</th>
                                </tr>
                            </thead>
                            <tbody>
                                {prices.map((price, index) => (
                                    <tr key={price.id}>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {price.unit?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatCurrency(price.price)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatThousands(
                                                    price.conversion_factor,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            {price.is_default ? (
                                                <Chip
                                                    color="primary"
                                                    size="sm"
                                                    variant="flat"
                                                >
                                                    Default
                                                </Chip>
                                            ) : (
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    -
                                                </ThemedText>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Stok per Gudang */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Stok per Gudang
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {stockSummary.length} entri
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 w-8 shrink-0 rounded-lg" />
                                    <Skeleton className="h-5 flex-2 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : stockSummary.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Belum ada data stok.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Gudang</th>
                                    <th>Satuan</th>
                                    <th>Masuk</th>
                                    <th>Keluar</th>
                                    <th>Tersedia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockSummary.map((stock, index) => (
                                    <tr key={index}>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {stock.warehouse?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {stock.unit?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatThousands(
                                                    stock.total_in,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatThousands(
                                                    stock.total_out,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className="font-semibold"
                                            >
                                                {formatThousands(
                                                    stock.available,
                                                )}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Galeri Gambar */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Galeri Gambar
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {images.length} gambar
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="p-4 pt-2">
                    {isLoading ? (
                        <div className="flex gap-3">
                            {[...Array(3)].map((_, i) => (
                                <Skeleton
                                    key={i}
                                    className="h-28 w-28 rounded-lg"
                                />
                            ))}
                        </div>
                    ) : images.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Tidak ada gambar.
                        </ThemedText>
                    ) : (
                        <div className="flex flex-wrap gap-3">
                            {images.map((img, index) => (
                                <Image
                                    key={img.id}
                                    src={img.url_600}
                                    alt={`Gambar ${index + 1}`}
                                    className="h-28 w-28 cursor-pointer object-cover"
                                    radius="lg"
                                    onClick={() =>
                                        setLightbox({
                                            isOpen: true,
                                            index,
                                        })
                                    }
                                    fallbackSrc={null}
                                />
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Footer Metadata */}
            {!isLoading && material && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
                    <ThemedText size="xs" variant="secondary">
                        Dibuat:{" "}
                        {formatDate(material.created_at, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        ·
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        Terakhir diubah:{" "}
                        {formatDate(material.updated_at, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </ThemedText>
                </div>
            )}

            {/* Image Lightbox */}
            <ImageLightbox
                isOpen={lightbox.isOpen}
                onClose={() => setLightbox({ isOpen: false, index: 0 })}
                images={images}
                urlKey="url_1000"
                initialIndex={lightbox.index}
            />
        </div>
    );
};

const InfoItem = ({ label, value, bold = false }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm" className={bold ? "font-semibold" : ""}>
            {value}
        </ThemedText>
    </div>
);

export default ProductDetail;
